import { useState } from "react";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { useQuery } from "@tanstack/react-query";
import { callPymbleApi } from "./usePymbleApi";

// S2 endpoints — editor groups and dictionary
const EDITOR_GROUPS_ENDPOINT = "my/groups/";
const GROUP_TYPES_ENDPOINT = "dictionary/GroupTypes";

/**
 * Hook to fetch editor groups and group types from S2 API.
 */
export function useEditorGroups(adminGroups?: string[]) {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const [groups, setGroups] = useState<any>([]);
	const [groupsLoaded, setGroupsLoaded] = useState(false);
	const { instance } = useMsal();
	const isAuthenticated = useIsAuthenticated();

	// Group types — fetch once on mount
	const { data: groupTypes = [] } = useQuery({
		queryKey: ["groupTypes"],
		queryFn: async () => {
			const res = await callPymbleApi(
				instance,
				"GET",
				GROUP_TYPES_ENDPOINT
			);
			if (!res) return [];
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			const groupTypesArray: any[] = [];
			for (const e of res) {
				const data = e["Data"];
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				data.forEach((type: any) => {
					groupTypesArray.push({
						id: type["LocalId"],
						value: type["Name"],
						type: type["TypeId"],
					});
				});
			}
			groupTypesArray.sort((a, b) => a.value.localeCompare(b.value));
			return groupTypesArray;
		},
		enabled: isAuthenticated,
	});

	// Editor groups — called manually with search text
	const getData = async (searchText: string, groupType?: string) => {
		if (searchText) {
			setGroupsLoaded(false);
			const endpoint =
				EDITOR_GROUPS_ENDPOINT +
				"?editors_only=true&q=" +
				searchText +
				(adminGroups ? "&g=" + adminGroups : "") +
				(groupType ? "&group_type=" + groupType : "");

			const res = await callPymbleApi(instance, "GET", endpoint);
			if (res) {
				const groupArray = [];
				for (const e of res) {
					groupArray.push({
						value: e["_id"],
						label: e["Title"] || e["Name"]?.["Short"] || "",
						MemberCount:
							e["GroupMemberCount"] || e["MemberCount"] || 0,
						Icon: e["Icon"],
					});
				}
				groupArray.sort((a, b) => a.label.localeCompare(b.label));
				setGroups(groupArray);
				return groupArray;
			}
			setGroupsLoaded(true);
		}
	};

	return {
		getData,
		groups,
		groupTypes,
		groupsLoaded,
	};
}
